
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { supabase } from "@/lib/supabase";
import { Helmet } from "react-helmet-async";
import { Lock, Check, EyeOff, Eye, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ImageWithFallback } from "@/components/common/ImageWithFallback";

const formSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenType, setTokenType] = useState<string | null>(null);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Extract token and error info from URL on component mount
  useEffect(() => {
    const extractDataFromUrl = () => {
      console.log("URL hash:", location.hash);
      console.log("URL search:", location.search);
      
      // First check for token in the URL parameters 
      const searchParams = new URLSearchParams(location.search);
      let extractedToken = searchParams.get("token") || null;
      let type = searchParams.get("type") || null;
      
      // If no token in search params, try to get from hash
      if (!extractedToken && location.hash) {
        // Parse the hash - handling Supabase's format
        const hashParams = new URLSearchParams(location.hash.substring(1));
        extractedToken = hashParams.get("access_token") || null;
        
        // Check for error in hash
        const errorInHash = hashParams.get("error");
        const errorCode = hashParams.get("error_code");
        const errorDescription = hashParams.get("error_description");
        
        if (errorInHash) {
          console.error("Error in URL hash:", {
            error: errorInHash,
            error_code: errorCode,
            error_description: errorDescription
          });
          
          if (errorCode === "otp_expired") {
            setError("Your password reset link has expired. Please request a new link.");
            return;
          }
          
          setError(errorDescription || "An error occurred with your reset link. Please request a new one.");
          return;
        }
      }
      
      if (extractedToken) {
        console.log("Token found in URL");
        setToken(extractedToken);
        if (type) {
          setTokenType(type);
        }
      } else {
        console.log("No token found in URL");
        // Check if user is already authenticated - this would happen if they're coming back after a session refresh
        checkCurrentSession();
      }
    };
    
    const checkCurrentSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        console.log("User already has an active session");
        // If they're already logged in and just trying to reset password
        setToken("session_active");
      } else {
        setError("No reset token found. Please request a new password reset link.");
      }
    };
    
    extractDataFromUrl();
  }, [location]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Attempting to reset password");
      
      if (!token) {
        setError("No valid reset token found. Please request a new password reset link.");
        setIsLoading(false);
        return;
      }
      
      let updateResult;
      
      if (token === "session_active") {
        // User already has a session, use it to update password
        console.log("Updating password with active session");
        updateResult = await supabase.auth.updateUser({ password: values.password });
      } else {
        // Use the token from URL to authenticate first, then update
        // This is for recovery flow where user doesn't have an active session
        console.log("Using recovery token to update password");
        updateResult = await supabase.auth.updateUser({ 
          password: values.password 
        });
      }

      if (updateResult.error) {
        console.error("Password reset error:", updateResult.error);
        
        if (updateResult.error.message.includes("expired") || 
            updateResult.error.message.includes("invalid")) {
          setError("Your reset link has expired. Please request a new password reset link.");
        } else {
          setError(updateResult.error.message || "Failed to reset password. Please try again.");
        }
        
        toast.error("Failed to reset password");
      } else {
        console.log("Password reset successful");
        setIsSuccess(true);
        toast.success("Password reset successful!");
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate("/auth/login");
        }, 3000);
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      setError("An unexpected error occurred. Please try again or contact support.");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRequestNewLink = () => {
    navigate("/auth/login", { state: { openForgotPassword: true } });
  };

  return (
    <>
      <Helmet>
        <title>Reset Password | Trade Bot 365</title>
      </Helmet>
      <div className="flex min-h-screen w-full items-center justify-center bg-gradient-to-br from-botscheme-royal-purple/10 via-background to-botscheme-emerald-green/10">
        <div className="relative w-full max-w-md px-4">
          {/* Decorative elements */}
          <div className="absolute -z-10 h-56 w-56 -left-10 top-10 rounded-full bg-botscheme-royal-purple/20 blur-3xl"></div>
          <div className="absolute right-0 bottom-10 -z-10 h-72 w-72 rounded-full bg-botscheme-emerald-green/20 blur-3xl"></div>
          
          <Card className="bg-white/5 backdrop-blur-xl shadow-xl border border-white/10">
            <CardHeader className="space-y-1">
              <div className="mb-6 flex justify-center">
                <div className="w-64 h-24 relative">
                  <ImageWithFallback 
                    src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png" 
                    alt="Trade Bot 365 Logo" 
                    className="w-full h-full object-contain"
                    loading="eager"
                    skeletonClassName="bg-muted/50"
                  />
                </div>
              </div>
              <CardTitle className="text-2xl font-semibold text-center">Reset Password</CardTitle>
              <CardDescription className="text-center">
                Enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isSuccess ? (
                <Alert className="bg-green-500/10 border border-green-500/30 text-green-500">
                  <Check className="h-4 w-4 mt-0.5" />
                  <AlertTitle>Password Reset Successful</AlertTitle>
                  <AlertDescription>
                    Your password has been reset successfully. You will be redirected to the login page in a moment.
                  </AlertDescription>
                </Alert>
              ) : error && error.includes("expired") ? (
                <div className="space-y-4">
                  <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                    <AlertTitle>Link Expired</AlertTitle>
                    <AlertDescription>
                      {error}
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground">
                          Password reset links are only valid for a limited time. Please request a new reset link.
                        </p>
                      </div>
                    </AlertDescription>
                  </Alert>
                  <div className="flex justify-center">
                    <Button 
                      variant="default" 
                      onClick={handleRequestNewLink}
                      className="gap-2"
                    >
                      <RefreshCcw className="h-4 w-4" />
                      Request New Reset Link
                    </Button>
                  </div>
                </div>
              ) : error ? (
                <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                  <AlertDescription>
                    {error}
                    <div className="mt-2">
                      <Button 
                        variant="link" 
                        className="px-0 text-destructive hover:text-destructive/70"
                        onClick={() => navigate("/auth/login")}
                      >
                        Return to login page
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                type={showPassword ? "text" : "password"} 
                                placeholder="••••••••" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                            <button 
                              type="button" 
                              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <div className="relative">
                            <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                            <FormControl>
                              <Input 
                                type={showConfirmPassword ? "text" : "password"} 
                                placeholder="••••••••" 
                                className="pl-10 bg-white/5 border-white/10 focus:border-botscheme-ocean-blue transition-all" 
                                {...field} 
                              />
                            </FormControl>
                            <button 
                              type="button" 
                              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground transition-colors"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            >
                              {showConfirmPassword ? (
                                <EyeOff className="h-5 w-5" />
                              ) : (
                                <Eye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-botscheme-ocean-blue to-botscheme-royal-purple hover:opacity-90 transition-all shadow-lg" 
                      disabled={isLoading || !token || !!error}
                    >
                      {isLoading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Resetting Password...
                        </span>
                      ) : "Reset Password"}
                    </Button>
                  </form>
                </Form>
              )}
            </CardContent>
            <CardFooter className="flex justify-center pb-6 pt-2">
              <Button 
                variant="link" 
                className="text-botscheme-ocean-blue hover:text-botscheme-royal-purple transition-colors"
                onClick={() => navigate("/auth/login")}
              >
                Back to Login
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordPage;
